<template>
  <VTDropdownPill
    :disable-save-button="false"
    :is-enabled="isEditable"
    @confirm="handleSave"
    @close="closeDropdown = false"
    @clear="handleReset"
    @click="handlePropagation"
  >
    <template #dropDownHeader>
      <VTPill label="Tags">
        <template #values>
          <div :class="$style.wrapper">
            <div>
              <div :class="$style.row">
                <div :class="[$style.valuesContainer, $style.timeText]">
                  <Chip v-for="tag in localTags" :key="tag.id" :label="tag.name" class="p-mr-2" />
                  <Chip v-if="hasMore" :label="`+${remainingCount}`" class="p-mr-2" />
                </div>
              </div>
            </div>
          </div>
        </template>
      </VTPill>
    </template>
    <template #dropdown-content>
      <div :class="$style.managerWrapper">
        <p>Tags</p>
        <TagsManager
          :model-value="tags"
          :game-id="gameId"
          :version-id="versionId"
          :max-visible-tags="2"
          :has-borders="false"
          :type="'OFFER'"
          @update:model-value="handleTagUpdate"
        />
      </div>
    </template>
  </VTDropdownPill>
</template>

<script setup lang="ts">
import TagsManager from "@/components/TagsManager/TagsManager.vue";
import VTDropdownPill from "@/components/VTDropdownPill";
import VTPill from "@/components/VTPill/VTPill.vue";
import Chip from "primevue/chip";
import type { Ref } from "vue";
import { computed, ref } from "vue";

const props = defineProps({
  tags: {
    type: Array,
    default: () => [],
  },
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  isEditable: {
    type: Boolean,
    default: true,
  },
  maxVisibleTags: {
    type: Number,
    default: 4,
  },
});

const emit = defineEmits(["update:tags"]);

interface Tag {
  id: string;
  name: string;
}

const localTags: Ref<Tag[]> = ref(props.tags as Tag[]);

function handleTagUpdate(tags: Tag[]) {
  localTags.value = [...tags];

  handleSave();
}

function handlePropagation(event: MouseEvent) {
  event.stopPropagation();
}

async function handleSave() {
  emit(
    "update:tags",
    localTags.value.map((tag: { id: string; name: string }) => ({ id: tag.id, name: tag.name }))
  );
}

function handleReset() {
  localTags.value = [];
  handleSave();
}

const hasMore = computed(() => localTags.value.length > props.maxVisibleTags);
const remainingCount = computed(() => localTags.value.length - props.maxVisibleTags);
</script>

<style module>
.tagSection {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.chip {
  background-color: var(--vd-color-blue-light-9);
  border-radius: 8px;
}

.label {
  font-size: 14px;
  font-weight: 600;
  color: var(--vd-color-text-light-2);
}

.tagManager {
  width: 300px;
}

.managerWrapper {
  margin-bottom: 10px;
}
</style>
